import React from 'react';
import { Box, Button, IconButton, Link as MuiLink, Stack, TextField } from '@mui/material';
import DeleteIcon from '@mui/icons-material/DeleteOutline';
import AddIcon from '@mui/icons-material/Add';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';

import { OperationDocLink } from '../../services/OperationProcessService';

/**
 * URL 정규화 — 스킴이 없으면 https:// 를 붙여 새 탭 링크가 상대경로로
 * 깨지지 않게 한다. 빈 문자열이면 빈 문자열을 반환한다.
 * export 와 셀 양쪽에서 같은 규칙을 쓰기 위해 외부로 노출한다.
 */
export function normalizeDocLinkHref(url: string): string {
  const trimmed = (url ?? '').trim();
  if (!trimmed) return '';
  if (/^(https?:\/\/|mailto:)/i.test(trimmed)) return trimmed;
  return `https://${trimmed}`;
}

interface OperationDocLinksCellProps {
  links: OperationDocLink[];
  editable: boolean;
  onChange: (links: OperationDocLink[]) => void;
}

/**
 * 운영 프로세스 행의 "관련 문서 링크" 셀.
 *
 * - 읽기 전용: 클릭 가능한 링크 목록(새 탭).
 * - 편집: 이름(선택)+URL 입력 행을 여러 개 추가/삭제.
 */
export default function OperationDocLinksCell({
  links,
  editable,
  onChange,
}: OperationDocLinksCellProps) {
  const list = Array.isArray(links) ? links : [];

  if (!editable) {
    const visible = list.filter((l) => (l.url ?? '').trim());
    if (visible.length === 0) {
      return <Box sx={{ color: 'text.disabled', fontSize: 13 }}>—</Box>;
    }
    return (
      <Stack spacing={0.5}>
        {visible.map((l, i) => {
          const href = normalizeDocLinkHref(l.url);
          const text = (l.label ?? '').trim() || l.url;
          return (
            <MuiLink
              key={i}
              href={href}
              target="_blank"
              rel="noopener noreferrer"
              sx={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 0.5,
                fontSize: 13,
                wordBreak: 'break-all',
              }}
            >
              <OpenInNewIcon sx={{ fontSize: 14 }} />
              {text}
            </MuiLink>
          );
        })}
      </Stack>
    );
  }

  const updateAt = (idx: number, patch: Partial<OperationDocLink>) => {
    onChange(list.map((l, i) => (i === idx ? { ...l, ...patch } : l)));
  };
  const removeAt = (idx: number) => {
    onChange(list.filter((_, i) => i !== idx));
  };
  const add = () => {
    onChange([...list, { label: '', url: '' }]);
  };

  return (
    <Stack spacing={0.75}>
      {list.map((l, i) => (
        <Stack key={i} direction="row" spacing={0.5} alignItems="center">
          <TextField
            value={l.label ?? ''}
            onChange={(e) => updateAt(i, { label: e.target.value })}
            placeholder="이름(선택)"
            variant="outlined"
            size="small"
            sx={{ width: 110, flexShrink: 0 }}
          />
          <TextField
            value={l.url ?? ''}
            onChange={(e) => updateAt(i, { url: e.target.value })}
            placeholder="https://..."
            variant="outlined"
            size="small"
            fullWidth
          />
          <IconButton size="small" onClick={() => removeAt(i)} aria-label="링크 삭제">
            <DeleteIcon fontSize="small" />
          </IconButton>
        </Stack>
      ))}
      <Button size="small" startIcon={<AddIcon />} onClick={add} sx={{ alignSelf: 'flex-start' }}>
        링크 추가
      </Button>
    </Stack>
  );
}
